import { useRouter } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
} from "react-native"
import * as dateFunctions from "date-fns"

import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import * as Models from "@/redux/models"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import { useShallowEqualSelector } from "@/redux/useRootSelector"
import {
  getMinDateForWindow,
  getTimelineDayBuckets,
  getTimelineWindowFromState,
  getIsTimelineLoading,
  loadFarmTimelineEventsAsync,
  setTimelineWindow,
  TIMELINE_WINDOW_OPTIONS,
} from "@/redux/farm-timeline.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import {
  AppIcons,
  AppText,
  Box,
  Card,
  Heading,
  Row,
  SPACING,
  Theme,
} from "@/theme"

import type {
  TimelineAlarmBurstEntry,
  TimelineDayBucket,
  TimelineEntry,
  TimelineSessionEntry,
  TimelineTransitionEntry,
  TimelineWindow,
} from "@/redux/farm-timeline.reducer"

const HEADER_DATE_FORMAT = "EEE, MMM d"
const ITEM_TIME_FORMAT = "h:mm a"

function useTexts() {
  return useTranslation("farmTimeline")
}

function formatDuration(durationMs: number | null): string | null {
  if (durationMs == null || durationMs <= 0) return null
  const duration = dateFunctions.intervalToDuration({
    end: durationMs,
    start: 0,
  })
  return dateFunctions.formatDuration(duration, {
    format: ["days", "hours", "minutes"],
    zero: false,
  })
}

function getDeviceName(
  deviceId: string,
  configurations: Record<string, DeviceConfiguration | undefined>,
): string {
  const config = configurations[deviceId]
  if (!config) return deviceId
  return config.deviceName ?? config.codaDeviceAlias ?? deviceId
}

function getEntryIcon(entry: TimelineEntry): keyof typeof AppIcons {
  if (entry.kind === "session") {
    switch (entry.signal) {
      case "pressure":
        return "Gauge"
      case "reel":
        return "DeviceReel"
      case "relay":
        return "SwitchClosed"
    }
  }
  return "Warning"
}

function getEntryColor(entry: TimelineEntry): string {
  if (entry.kind === "alarm_burst" || entry.kind === "transition") {
    return Theme.COLORS.$error[500]
  }
  return Theme.COLORS.$darkBlue[600]
}

interface EntryRowProps {
  configurations: Record<string, DeviceConfiguration | undefined>
  entry: TimelineEntry
  onPress: (deviceId: string) => void
}

function SessionDescription({
  entry,
  deviceName,
}: {
  entry: TimelineSessionEntry
  deviceName: string
}): React.JSX.Element {
  const { t } = useTexts()
  const duration = formatDuration(entry.durationMs)
  const key =
    entry.endTimestamp == null
      ? (`session.${entry.signal}.ongoingWithDevice` as const)
      : (`session.${entry.signal}.completedWithDeviceAndDuration` as const)
  return (
    <AppText fontSize={"$md"}>
      {t(key, {
        deviceName,
        duration: duration ?? t("session.briefRun"),
      })}
    </AppText>
  )
}

function TransitionDescription({
  entry,
  deviceName,
}: {
  entry: TimelineTransitionEntry
  deviceName: string
}): React.JSX.Element {
  const { t } = useTexts()
  const key = `transition.${entry.transitionType}.withDevice` as const
  return <AppText fontSize={"$md"}>{t(key, { deviceName })}</AppText>
}

function BurstDescription({
  entry,
  deviceName,
}: {
  entry: TimelineAlarmBurstEntry
  deviceName: string
}): React.JSX.Element {
  const { t } = useTexts()
  const key = `burst.${entry.transitionType}.withDeviceAndCount` as const
  return (
    <AppText fontSize={"$md"}>
      {t(key, { count: entry.count, deviceName })}
    </AppText>
  )
}

function EntryRow({
  configurations,
  entry,
  onPress,
}: EntryRowProps): React.JSX.Element {
  const deviceName = getDeviceName(entry.deviceId, configurations)
  const iconName = getEntryIcon(entry)
  const IconComponent = AppIcons[iconName]
  const color = getEntryColor(entry)

  const time = (() => {
    if (entry.kind === "session") {
      const start = dateFunctions.format(
        new Date(entry.startTimestamp),
        ITEM_TIME_FORMAT,
      )
      if (entry.endTimestamp == null) return start
      const end = dateFunctions.format(
        new Date(entry.endTimestamp),
        ITEM_TIME_FORMAT,
      )
      return `${start} – ${end}`
    }
    if (entry.kind === "alarm_burst") {
      const start = dateFunctions.format(
        new Date(entry.startTimestamp),
        ITEM_TIME_FORMAT,
      )
      const end = dateFunctions.format(
        new Date(entry.endTimestamp),
        ITEM_TIME_FORMAT,
      )
      return `${start} – ${end}`
    }
    return dateFunctions.format(new Date(entry.timestamp), ITEM_TIME_FORMAT)
  })()

  return (
    <Pressable onPress={() => onPress(entry.deviceId)}>
      <Card style={styles.entryCard}>
        <Row alignItems={"center"}>
          <Box mr={"$3"}>
            <IconComponent color={color} size={26} />
          </Box>
          <Box flex={1}>
            {entry.kind === "session" ? (
              <SessionDescription deviceName={deviceName} entry={entry} />
            ) : entry.kind === "alarm_burst" ? (
              <BurstDescription deviceName={deviceName} entry={entry} />
            ) : (
              <TransitionDescription deviceName={deviceName} entry={entry} />
            )}
            <AppText colorScheme={"secondary"} fontSize={"$sm"}>
              {time}
            </AppText>
          </Box>
        </Row>
      </Card>
    </Pressable>
  )
}

function BucketHeader({
  bucket,
  today,
}: {
  bucket: TimelineDayBucket
  today: Date
}): React.JSX.Element {
  const { t } = useTexts()
  let label: string
  if (dateFunctions.isSameDay(bucket.date, today)) {
    label = t("today")
  } else if (
    dateFunctions.isSameDay(bucket.date, dateFunctions.subDays(today, 1))
  ) {
    label = t("yesterday")
  } else {
    label = dateFunctions.format(bucket.date, HEADER_DATE_FORMAT)
  }
  return (
    <Heading colorScheme={"secondary"} variant={"h5"}>
      {label}
    </Heading>
  )
}

function WindowPicker({
  value,
  onChange,
}: {
  value: TimelineWindow
  onChange: (window: TimelineWindow) => void
}): React.JSX.Element {
  const { t } = useTexts()
  return (
    <Row style={styles.windowPicker}>
      {TIMELINE_WINDOW_OPTIONS.map((option) => {
        const isSelected = option === value
        return (
          <Pressable
            key={option}
            style={[
              styles.windowOption,
              isSelected ? styles.windowOptionSelected : undefined,
            ]}
            onPress={() => onChange(option)}
          >
            <AppText
              fontSize={"$sm"}
              style={isSelected ? styles.windowOptionTextSelected : undefined}
            >
              {t(`window.${option}`)}
            </AppText>
          </Pressable>
        )
      })}
    </Row>
  )
}

/**
 * Reusable timeline body: window picker + bucketed list. No outer chrome —
 * the wrapping screen (native) or sidebar layout (web) is responsible for
 * providing the title bar, safe-area handling, and any back/close affordance.
 */
export function TimelineContent(): React.JSX.Element {
  const { t } = useTexts()
  const router = useRouter()
  const dispatch = useRootDispatch()
  const { handleError } = useRequestHelpers("LoadFarmTimelineEvents")

  const window = useShallowEqualSelector(getTimelineWindowFromState)
  const buckets = useShallowEqualSelector(getTimelineDayBuckets)
  const isLoading = useShallowEqualSelector(getIsTimelineLoading)
  const configurations = useShallowEqualSelector(
    Models.deviceConfiguration.selectEntities,
  )

  const handleFetch = React.useCallback(() => {
    const minDate = getMinDateForWindow(window)
    dispatch(
      loadFarmTimelineEventsAsync({
        minDate: minDate.toISOString(),
      }),
    )
      .unwrap()
      .catch((error) => handleError(error, { toastMessage: "default" }))
  }, [dispatch, handleError, window])

  React.useEffect(() => {
    handleFetch()
  }, [handleFetch])

  const handleWindowChange = React.useCallback(
    (next: TimelineWindow) => {
      dispatch(setTimelineWindow(next))
    },
    [dispatch],
  )

  const handlePressEntry = React.useCallback(
    (deviceId: string) => {
      const config = configurations[deviceId]
      if (config?.codaDeviceAlias) {
        router.push(
          `/devices/${config.codaDeviceAlias}/event-history` as never,
        )
      }
    },
    [configurations, router],
  )

  const today = React.useMemo(() => dateFunctions.startOfDay(new Date()), [])

  return (
    <Box flex={1}>
      <Box px={"$4"} py={"$2"}>
        <WindowPicker value={window} onChange={handleWindowChange} />
      </Box>
      <FlatList
        contentContainerStyle={styles.listContent}
        data={buckets}
        keyExtractor={(bucket) => bucket.key}
        refreshControl={
          <RefreshControl refreshing={isLoading} onRefresh={handleFetch} />
        }
        ListEmptyComponent={
          isLoading ? null : (
            <Box style={styles.emptyState}>
              <AppText colorScheme={"secondary"}>{t("empty")}</AppText>
            </Box>
          )
        }
        renderItem={({ item: bucket }) => (
          <Box>
            <Row style={styles.bucketHeader}>
              <BucketHeader bucket={bucket} today={today} />
            </Row>
            {bucket.entries.map((entry) => (
              <EntryRow
                key={entry.key}
                configurations={configurations}
                entry={entry}
                onPress={handlePressEntry}
              />
            ))}
          </Box>
        )}
      />
    </Box>
  )
}

const styles = StyleSheet.create({
  bucketHeader: {
    paddingBottom: SPACING.$2,
    paddingHorizontal: SPACING.$4,
    paddingTop: SPACING.$4,
  },
  emptyState: {
    alignItems: "center",
    paddingHorizontal: SPACING.$4,
    paddingVertical: SPACING.$8,
  },
  entryCard: {
    marginBottom: SPACING.$2,
    marginHorizontal: SPACING.$4,
  },
  listContent: {
    paddingBottom: SPACING.$8,
  },
  windowOption: {
    alignItems: "center",
    borderRadius: 999,
    flex: 1,
    paddingHorizontal: SPACING.$3,
    paddingVertical: SPACING.$2,
  },
  windowOptionSelected: {
    backgroundColor: Theme.COLORS.$darkBlue[600],
  },
  windowOptionTextSelected: {
    color: Theme.COLORS.$white,
    fontWeight: "600",
  },
  windowPicker: {
    backgroundColor: Theme.COLORS.$gray[100],
    borderRadius: 999,
    padding: SPACING.$1,
  },
})
